import {
  readRunDetailUrl,
  RunDetailUrlState,
  writeRunDetailUrl,
} from './run-detail-url';
import { useSearchParams } from '@/lib/router-helpers';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type RunDetailSearchState = {
  tab: string | undefined;
  view: 'timeline' | 'graph';
  focusedTaskRunId: string | undefined;
  selectedAttempt: number | undefined;
  selectedSpanId: string | undefined;
  selectedGroupId: string | undefined;
  queryString: string;
  showErrors: boolean;
  setTab: (tab: string) => void;
  setView: (view: 'timeline' | 'graph') => void;
  setFocusedTaskRunId: (id: string | undefined) => void;
  setSelectedAttempt: (attempt: number | undefined) => void;
  setSelectedSpanId: (id: string | undefined) => void;
  setSelectedGroupId: (id: string | undefined) => void;
  setQueryString: (q: string) => void;
  setShowErrors: (show: boolean) => void;
  set: (patch: {
    tab?: string;
    view?: 'timeline' | 'graph';
    focusedTaskRunId?: string;
    selectedAttempt?: number;
    selectedSpanId?: string;
    selectedGroupId?: string;
    queryString?: string;
    showErrors?: boolean;
  }) => void;
};

const RunDetailSearchContext = createContext<RunDetailSearchState | null>(null);

export function RunDetailSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => readRunDetailUrl(searchParams), [searchParams]);

  const update = useCallback(
    (patch: Partial<RunDetailUrlState>) => {
      setSearchParams((prev) => {
        const current = readRunDetailUrl(prev);
        const next = { ...current, ...patch };

        return writeRunDetailUrl(
          prev,
          next.panel,
          next.task,
          next.attempt,
          next.span,
          next.group,
          next.view,
          next.q,
          next.errors,
        );
      });
    },
    [setSearchParams],
  );

  const value = useMemo<RunDetailSearchState>(
    () => ({
      tab: state.panel,
      view: state.view ?? 'timeline',
      focusedTaskRunId: state.task,
      selectedAttempt: state.attempt,
      selectedSpanId: state.span,
      selectedGroupId: state.group,
      queryString: state.q ?? '',
      showErrors: state.errors ?? false,
      setTab: (tab: string) => update({ panel: tab }),
      setView: (view: 'timeline' | 'graph') => update({ view }),
      setFocusedTaskRunId: (id: string | undefined) => update({ task: id }),
      setSelectedAttempt: (attempt: number | undefined) => update({ attempt }),
      setSelectedSpanId: (id: string | undefined) =>
        update({ span: id, group: undefined }),
      setSelectedGroupId: (id: string | undefined) =>
        update({ group: id, span: undefined }),
      setQueryString: (q: string) => update({ q: q || undefined }),
      setShowErrors: (show: boolean) => update({ errors: show }),
      set: (patch) =>
        update({
          ...('tab' in patch ? { panel: patch.tab } : {}),
          ...('view' in patch ? { view: patch.view } : {}),
          ...('focusedTaskRunId' in patch
            ? { task: patch.focusedTaskRunId }
            : {}),
          ...('selectedAttempt' in patch
            ? { attempt: patch.selectedAttempt }
            : {}),
          ...('selectedSpanId' in patch
            ? { span: patch.selectedSpanId, group: undefined }
            : {}),
          ...('selectedGroupId' in patch
            ? { group: patch.selectedGroupId, span: undefined }
            : {}),
          ...('queryString' in patch ? { q: patch.queryString } : {}),
          ...('showErrors' in patch ? { errors: patch.showErrors } : {}),
        }),
    }),
    [state, update],
  );

  return (
    <RunDetailSearchContext.Provider value={value}>
      {children}
    </RunDetailSearchContext.Provider>
  );
}

export function RunDetailSearchLocalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [tab, setTab] = useState<string | undefined>();
  const [view, setView] = useState<'timeline' | 'graph'>('timeline');
  const [focusedTaskRunId, setFocusedTaskRunId] = useState<
    string | undefined
  >();
  const [selectedAttempt, setSelectedAttempt] = useState<number | undefined>();
  const [selectedSpanId, setSelectedSpanIdRaw] = useState<string | undefined>();
  const [selectedGroupId, setSelectedGroupIdRaw] = useState<
    string | undefined
  >();
  const [queryString, setQueryString] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const value = useMemo<RunDetailSearchState>(
    () => ({
      tab,
      view,
      focusedTaskRunId,
      selectedAttempt,
      selectedSpanId,
      selectedGroupId,
      queryString,
      showErrors,
      setTab,
      setView,
      setFocusedTaskRunId,
      setSelectedAttempt,
      setSelectedSpanId: (id: string | undefined) => {
        setSelectedSpanIdRaw(id);
        setSelectedGroupIdRaw(undefined);
      },
      setSelectedGroupId: (id: string | undefined) => {
        setSelectedGroupIdRaw(id);
        setSelectedSpanIdRaw(undefined);
      },
      setQueryString,
      setShowErrors,
      set: (patch) => {
        if ('tab' in patch) {
          setTab(patch.tab);
        }
        if ('focusedTaskRunId' in patch) {
          setFocusedTaskRunId(patch.focusedTaskRunId);
        }
        if ('view' in patch && patch.view) {
          setView(patch.view);
        }
        if ('selectedAttempt' in patch) {
          setSelectedAttempt(patch.selectedAttempt);
        }
        if ('selectedSpanId' in patch) {
          setSelectedSpanIdRaw(patch.selectedSpanId);
          if (patch.selectedSpanId) {
            setSelectedGroupIdRaw(undefined);
          }
        }
        if ('selectedGroupId' in patch) {
          setSelectedGroupIdRaw(patch.selectedGroupId);
          if (patch.selectedGroupId) {
            setSelectedSpanIdRaw(undefined);
          }
        }
        if ('queryString' in patch) {
          setQueryString(patch.queryString ?? '');
        }
        if ('showErrors' in patch) {
          setShowErrors(patch.showErrors ?? false);
        }
      },
    }),
    [
      tab,
      view,
      focusedTaskRunId,
      selectedAttempt,
      selectedSpanId,
      selectedGroupId,
      queryString,
      showErrors,
    ],
  );

  return (
    <RunDetailSearchContext.Provider value={value}>
      {children}
    </RunDetailSearchContext.Provider>
  );
}

export function useRunDetailSearch(): RunDetailSearchState {
  const ctx = useContext(RunDetailSearchContext);
  if (!ctx) {
    throw new Error(
      'useRunDetailSearch must be used within a RunDetailSearchProvider',
    );
  }
  return ctx;
}
